import {fileHelper} from '../../../../gori/helpers'
import {A1_CONSTANTS} from '../core/constants'

const MAPPING_FIELDS = A1_CONSTANTS.A1_BATCH_UPLOAD_MAPPING.DEFAULT.filter((field) => field.value)

/**
 * The 43 headers the Create A1 Batch upload expects, in order, copied verbatim from
 * A1_Batch_Upload_Template.csv. A single wrong character makes the backend reject the file.
 */
export const A1_STANDARD_HEADERS = MAPPING_FIELDS.map((field) => field.header)

/** Field keys in the same order as A1_STANDARD_HEADERS. */
export const A1_STANDARD_FIELDS = MAPPING_FIELDS.map((field) => field.value)

/** Required field keys - used by the client-side pre-check before uploading. */
export const A1_REQUIRED_FIELDS = MAPPING_FIELDS.filter((field) => field.required).map(
  (field) => field.value
)

/** RFC 4180 escaping: quote a value when it holds a comma, quote or line break. */
const escapeCsvValue = (value: string) =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value

/**
 * Rebuilds the partner's CSV into the standard 43-column A1 template.
 *
 * The backend does not accept a `mappings` payload: it only stores mapping templates, so the
 * front end has to reorder the columns itself before uploading
 * (src/docs/partner-batch-mapping-api-fe.md, section 5).
 *
 * @param file      the CSV the partner picked
 * @param mappings  one entry per source column, in column order; the field key that column maps
 *                  to, or '' when the column is skipped
 * @returns a new File holding the standard 43-column CSV, or null when the source has no data rows
 */
export const buildStandardA1Csv = async (file: File, mappings: string[]): Promise<File | null> => {
  const rows = await fileHelper.readRowsAsArray(file)
  if (rows.length < 2) return null

  // field key -> index of the source column mapped to it. Duplicates are blocked by the UI
  // pre-check, so the first occurrence wins here.
  const sourceIndexByField = new Map<string, number>()
  mappings.forEach((field, index) => {
    if (field && !sourceIndexByField.has(field)) {
      sourceIndexByField.set(field, index)
    }
  })

  const dataRows = rows.slice(1)
  const standardRows = dataRows.map((row) =>
    A1_STANDARD_FIELDS.map((field) => {
      const sourceIndex = sourceIndexByField.get(field)
      // Unmapped fields stay as empty columns so the file always has all 43 of them.
      if (sourceIndex === undefined) return ''
      const cell = row[sourceIndex]
      return cell === undefined || cell === null ? '' : String(cell)
    })
  )

  // Written by hand rather than through xlsx: xlsx drops trailing empty cells, which would
  // leave rows with fewer than 43 columns, and the upload requires all of them.
  const csv = [A1_STANDARD_HEADERS, ...standardRows]
    .map((row) => row.map(escapeCsvValue).join(','))
    .join('\r\n')

  // BOM keeps Japanese/Chinese values readable for tools that assume the system codepage.
  const blob = new Blob(['﻿', csv], {type: 'text/csv;charset=utf-8;'})
  return new File([blob], file.name, {type: 'text/csv'})
}
